/**
 * Regroup a partner under a different parent, or promote it to top level.
 *
 * The confirm step exists because the consequence is invisible from the
 * partner's own record: regrouping changes which OTHER organisation can see
 * this partner's registrations. So the dialog will not let you confirm until it
 * has said, in words, who stops seeing how much.
 *
 * The parent picker searches server-side rather than loading a list. The
 * partner book runs to thousands of records on a real tenant, so any capped
 * fetch silently hides most of the valid choices.
 */

import { useMemo, useState } from 'react'
import {
  ROW_SELECTED,
  ROW_UNSELECTED,
} from '@/admin/pages/partnerships/selection-styles'
import { PartnerRowLabel } from '@/admin/pages/partnerships/partner-row-label'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'

import { usePartnerships } from '@/admin/hooks/use-partnerships'
import {
  useApplyRegroup,
  useRegroupPreview,
} from '@/admin/hooks/use-partner-regroup'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { toastApiError } from '@/shared/lib/api-error'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'

interface RegroupPartnerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partnershipUuid: string
  partnerName: string
  currentParentName: string | null
  onRegrouped?: () => void
}

// Sentinel for "no parent", kept distinct from "nothing picked yet": promoting
// to top level is a real choice and must be selectable.
const TOP_LEVEL = '__top_level__'

export function RegroupPartnerDialog({
  open,
  onOpenChange,
  partnershipUuid,
  partnerName,
  currentParentName,
  onRegrouped,
}: RegroupPartnerDialogProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [choice, setChoice] = useState<string | undefined>(undefined)
  const debouncedSearch = useDebouncedValue(search, 250)

  const { data: results, isFetching: searching } = usePartnerships(
    open && debouncedSearch.length >= 2
      ? { search: debouncedSearch, page_size: '10' }
      : undefined,
  )

  // Only the partner being moved is excluded. Suspended partners stay in the
  // list and are labelled: the backend's own rules refuse a CLOSED parent but
  // allow a suspended one, and filtering them out here made the picker empty
  // for every search on a tenant whose partners are activated one by one after
  // a migration - the feature looked broken while the data was fine.
  const candidates = useMemo(
    () => (results?.results ?? []).filter((p) => p.uuid !== partnershipUuid),
    [results, partnershipUuid],
  )

  const parentUuid =
    choice === undefined ? undefined : choice === TOP_LEVEL ? null : choice

  const {
    data: preview,
    isPending: previewPending,
    isError: previewFailed,
  } = useRegroupPreview(partnershipUuid, parentUuid)

  const apply = useApplyRegroup(partnershipUuid)

  const close = () => {
    setSearch('')
    setChoice(undefined)
    onOpenChange(false)
  }

  const confirm = async () => {
    if (choice === undefined) {
      toast.error(
        t(
          'partnerships.regroupPickFirst',
          'Choose where this partner should sit first.',
        ),
      )
      return
    }
    try {
      const applied = await apply.mutateAsync(parentUuid ?? null)
      // Undo is the SAME operation pointed back, so it re-runs the validation,
      // the drift repair and the audit row. A bare column write would put the
      // grouping back but leave the old parent still unable to see anything.
      const reverseTo: string | null =
        applied?.previous_parent_partnership_uuid || null
      const hadParent = !!applied?.previous_parent_name
      toast.success(t('partnerships.regroupDone', 'Partner regrouped.'), {
        action:
          reverseTo || !hadParent
            ? {
                label: t('common.undo', 'Undo'),
                onClick: () => {
                  void apply
                    .mutateAsync(reverseTo)
                    .then(() => {
                      onRegrouped?.()
                      toast.success(
                        t('partnerships.regroupUndone', 'Put back.'),
                      )
                    })
                    .catch((e) =>
                      toastApiError(
                        e,
                        t(
                          'partnerships.regroupUndoFailed',
                          'Could not put that back.',
                        ),
                      ),
                    )
                },
              }
            : undefined,
      })
      onRegrouped?.()
      close()
    } catch (err) {
      toastApiError(
        err,
        t('partnerships.regroupFailed', 'Could not regroup this partner.'),
      )
    }
  }

  const selectedLabel =
    choice === TOP_LEVEL
      ? t('partnerships.regroupTopLevel', 'Top level (no parent)')
      : candidates.find((p) => p.uuid === choice)?.downstream_organisation?.name

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-[560px]" data-testid="regroup-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('partnerships.regroupTitle', 'Regroup partner')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'partnerships.regroupIntro',
              'Choose which partner this one should sit under. This changes who can see its registrations.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-[13px] text-[var(--ink-2)]">
            <span className="font-medium text-[var(--ink)]">{partnerName}</span>
            {currentParentName ? (
              <>
                {' '}
                {t('partnerships.regroupCurrentlyUnder', 'currently sits under')}{' '}
                <span className="font-medium text-[var(--ink)]">
                  {currentParentName}
                </span>
                .
              </>
            ) : (
              <>
                {' '}
                {t(
                  'partnerships.regroupCurrentlyTopLevel',
                  'is currently at top level.',
                )}
              </>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="regroup-search">
              {t('partnerships.regroupNewParentLabel', 'New parent')}
            </Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
              <Input
                id="regroup-search"
                data-testid="regroup-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(
                  'partnerships.regroupSearchPlaceholder',
                  'Search partners by name',
                )}
                className="pl-8"
              />
            </div>

            <div className="max-h-[190px] overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)]">
              <Button
                variant="ghost"
                data-testid="regroup-option-top-level"
                onClick={() => setChoice(TOP_LEVEL)}
                className={`h-auto w-full justify-start rounded-none px-3 py-2 text-[13px] font-normal ${
                  choice === TOP_LEVEL ? ROW_SELECTED : ROW_UNSELECTED
                }`}
              >
                {t('partnerships.regroupTopLevel', 'Top level (no parent)')}
              </Button>

              {/* Loading is gated before the empty state: a cached-empty result
                  would otherwise flash "no partners" over an in-flight search. */}
              {debouncedSearch.length < 2 ? (
                <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
                  {t(
                    'partnerships.regroupSearchHint',
                    'Type at least two characters to find a partner.',
                  )}
                </p>
              ) : searching && candidates.length === 0 ? (
                <p className="flex items-center gap-2 px-3 py-3 text-[12px] text-[var(--ink-3)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('partnerships.regroupSearching', 'Searching')}
                </p>
              ) : candidates.length === 0 ? (
                <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
                  {t('partnerships.regroupNoMatches', 'No partners match that.')}
                </p>
              ) : (
                candidates.map((p) => (
                  <Button
                    key={p.uuid}
                    variant="ghost"
                    data-testid={`regroup-option-${p.uuid}`}
                    onClick={() => setChoice(p.uuid)}
                    className={`h-auto w-full justify-start rounded-none px-3 py-2 text-[13px] font-normal ${
                      choice === p.uuid ? ROW_SELECTED : ROW_UNSELECTED
                    }`}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <PartnerRowLabel
                        name={p.downstream_organisation?.name}
                        attributeSummary={p.attribute_summary}
                      />
                      {!p.is_active && (
                        <Badge variant="outline" className="shrink-0">
                          {t('partnerships.regroupSuspendedTag', 'Suspended')}
                        </Badge>
                      )}
                    </span>
                  </Button>
                ))
              )}
            </div>
          </div>

          {/* Impact. Loading first, then blocked, then the numbers - never a
              stale count next to a new choice. */}
          {choice !== undefined && (
            <div
              className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-3"
              data-testid="regroup-impact"
            >
              {previewPending ? (
                <p className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('partnerships.regroupChecking', 'Checking what changes')}
                </p>
              ) : previewFailed ? (
                <p className="text-[12px] text-[var(--status-error)]">
                  {t(
                    'partnerships.regroupPreviewFailed',
                    'Could not work out what this would change. Try again.',
                  )}
                </p>
              ) : preview && !preview.allowed ? (
                <p
                  className="text-[12px] text-[var(--status-error)]"
                  data-testid="regroup-blocked"
                >
                  {preview.blocked_reason}
                </p>
              ) : preview ? (
                <div className="space-y-1.5 text-[12px] text-[var(--ink-2)]">
                  <p className="flex items-center gap-1.5 text-[var(--ink)]">
                    <span className="font-medium">
                      {preview.previous_parent_name ??
                        t('partnerships.regroupTopLevelShort', 'Top level')}
                    </span>
                    <ArrowRight className="h-3 w-3 text-[var(--ink-3)]" />
                    <span className="font-medium">
                      {preview.new_parent_name ??
                        t('partnerships.regroupTopLevelShort', 'Top level')}
                    </span>
                  </p>
                  <p>
                    {t(
                      'partnerships.regroupRegistrationsAffected',
                      '{{registrations}} registrations move with this partner.',
                      {
                        registrations:
                          preview.registrations_affected.toLocaleString(),
                      },
                    )}
                  </p>
                  {preview.previous_parent_name && (
                    <p data-testid="regroup-loses-access">
                      {t(
                        'partnerships.regroupParentLosesAccess',
                        '{{parent}} will no longer see them.',
                        { parent: preview.previous_parent_name },
                      )}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={apply.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {/* Stays enabled with nothing picked - the click says what is
              missing. Only in-flight state and a refused move disable it. */}
          <Button
            onClick={confirm}
            data-testid="regroup-confirm"
            disabled={apply.isPending || (preview ? !preview.allowed : false)}
          >
            {apply.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            {selectedLabel
              ? t('partnerships.regroupConfirmNamed', 'Move under {{parent}}', {
                  parent: selectedLabel,
                })
              : t('partnerships.regroupConfirm', 'Regroup')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
